import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  HeartHandshake,
  LoaderCircle,
  LockKeyhole,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { MatchingQuestionnaireAnswers } from "./types";

interface MatchingQuestionnaireProps {
  onSubmit: (answers: MatchingQuestionnaireAnswers) => void | Promise<void>;
  isSubmitting: boolean;
  initialAnswers?: MatchingQuestionnaireAnswers;
  resumeAtEnd?: boolean;
  initialStarted?: boolean;
}

type ChoiceOption<T extends string> = {
  value: T;
  label: string;
  helper?: string;
};

const DEFAULT_ANSWERS: MatchingQuestionnaireAnswers = {
  population: "self",
  primaryConcern: "anxiety",
  secondaryConcerns: [],
  impact: "moderate",
  duration: "one_to_six_months",
  selfHarm: "no",
  preferredStyle: "not_sure",
  homework: "either",
  modality: "any",
  language: "bs",
  timePreferences: ["flexible"],
  budget: "no_limit",
  goals: "",
};

const STEPS = [
  {
    label: "Tema",
    title: "Krenimo nježno",
    eyebrow: "Korak 1 od 4",
    description: "Prvo razumijemo za koga tražite podršku i koja tema je trenutno najvažnija.",
    trust: "Odgovori nam služe samo da suzimo terapeute koji realno rade s ovom vrstom podrške.",
  },
  {
    label: "Stanje",
    title: "Koliko vas ovo opterećuje",
    eyebrow: "Korak 2 od 4",
    description: "Ovdje procjenjujemo hitnost i nivo podrške koji bi vam mogao trebati.",
    trust: "Ako postoji znak da vam treba brža ili hitna pomoć, to ćemo jasno označiti prije preporuka.",
  },
  {
    label: "Pristup",
    title: "Kakav rad bi vam najviše odgovarao",
    eyebrow: "Korak 3 od 4",
    description: "Neki terapeuti rade strukturisanije, neki više kroz razgovor i emocionalnu obradu.",
    trust: "Ove preferencije pomažu da preporuka bude ličnija, a ne samo lista dostupnih termina.",
  },
  {
    label: "Termin",
    title: "Kako se terapija može uklopiti u vaš život",
    eyebrow: "Korak 4 od 4",
    description: "Zadnji korak provjerava format, termin i cilj da preporučimo terapeute koje možete odmah rezervisati.",
    trust: "Ne morate napisati savršeno. Dovoljno je nekoliko rečenica o tome šta biste voljeli promijeniti.",
  },
];

const QUESTIONS = [
  {
    id: "population",
    stepIndex: 0,
    title: "Za koga tražite podršku?",
    description: "Ovo nam govori koje terapeute možemo uključiti u preporuku.",
    privacy: "Biramo terapeute koji rade s odraslima, djecom ili parovima/porodicama.",
  },
  {
    id: "primaryConcern",
    stepIndex: 0,
    title: "Koja tema vam je trenutno najvažnija?",
    description: "Odaberite ono što najviše liči na razlog zbog kojeg tražite prvi termin.",
    privacy: "Tema je glavni signal za izbor terapeuta koji ima relevantno iskustvo.",
  },
  {
    id: "secondaryConcerns",
    stepIndex: 0,
    title: "Postoje li još neke povezane teme?",
    description: "Opcionalno označite do 3 dodatne teme. Možete i samo nastaviti dalje.",
    privacy: "Dodatne teme samo fino podešavaju preporuku, nisu obavezne.",
  },
  {
    id: "impact",
    stepIndex: 1,
    title: "Koliko ova tema utiče na vaš svakodnevni život?",
    description: "Ne morate mjeriti savršeno. Odaberite najbliži opis.",
    privacy: "Intenzitet nam pomaže da procijenimo koliko brzo i kakva podrška ima smisla.",
  },
  {
    id: "duration",
    stepIndex: 1,
    title: "Koliko dugo traje?",
    description: "Trajanje pomaže da razlikujemo kratku krizu, duži obrazac ili hroničniji stres.",
    privacy: "Ovaj podatak ne služi za dijagnozu, nego za bolji kontekst preporuke.",
  },
  {
    id: "selfHarm",
    stepIndex: 1,
    title: "Da li imate misli da sebi naudite?",
    description: "Ovo pitanje je tu zbog vaše sigurnosti. Uzmite trenutak i odaberite iskreno.",
    privacy: "Ako postoji hitan rizik, preporuka se mijenja u sigurnosni korak.",
  },
  {
    id: "preferredStyle",
    stepIndex: 2,
    title: "Kakav stil terapeuta bi vam najviše odgovarao?",
    description: "Ovo ne zaključava izbor, već pomaže da predložimo način rada koji ima smisla za vas.",
    privacy: "Neki ljudi žele strukturu, neki topao razgovor, neki dublji rad. Sve opcije su validne.",
  },
  {
    id: "homework",
    stepIndex: 2,
    title: "Da li želite vježbe između susreta?",
    description: "Neki ljudi žele konkretne zadatke, drugi žele prostor za razgovor.",
    privacy: "Ovo pomaže da stil terapeuta ne bude naporan ili premalo konkretan.",
  },
  {
    id: "modality",
    stepIndex: 3,
    title: "Koji format vam odgovara?",
    description: "Koristimo ovo zajedno s dostupnim terminima kako biste mogli odmah rezervisati.",
    privacy: "Ako odaberete svejedno, preporuka daje prednost najboljem spoju i prvom dobrom terminu.",
  },
  {
    id: "timePreferences",
    stepIndex: 3,
    title: "Kada vam najčešće odgovara termin?",
    description: "Možete označiti više opcija. Barem jedna ostaje odabrana.",
    privacy: "Vrijeme koristimo da prikažemo terapeute koje realno možete rezervisati.",
  },
  {
    id: "budget",
    stepIndex: 3,
    title: "Budžet po susretu",
    description: "Ako niste sigurni, ostavite da cijena nije presudna.",
    privacy: "Budžet nam pomaže da preporuka bude praktična, a ne samo teorijski dobra.",
  },
  {
    id: "goals",
    stepIndex: 3,
    title: "Šta biste voljeli da se promijeni?",
    description: "Par rečenica je dovoljno. Možete i preskočiti ako ne znate šta biste napisali.",
    privacy: "Ovaj dio pomaže da preporuka bude ljudskija i bliža vašem stvarnom cilju.",
  },
] as const;

type QuestionId = (typeof QUESTIONS)[number]["id"];

const CONCERNS: ChoiceOption<MatchingQuestionnaireAnswers["primaryConcern"]>[] = [
  { value: "anxiety", label: "Anksioznost i brige", helper: "napetost, panika, stalno preispitivanje" },
  { value: "depression", label: "Tuga i bezvoljnost", helper: "pad energije, povlačenje, gubitak smisla" },
  { value: "burnout", label: "Burnout i stres", helper: "iscrpljenost, posao, pritisak obaveza" },
  { value: "trauma", label: "Trauma", helper: "teška iskustva, okidači, osjećaj nesigurnosti" },
  { value: "relationships", label: "Odnosi", helper: "partnerstvo, bliskost, konflikti" },
  { value: "family", label: "Porodica", helper: "porodična dinamika, granice, komunikacija" },
  { value: "self_esteem", label: "Samopouzdanje", helper: "kritičnost prema sebi, nesigurnost" },
  { value: "grief", label: "Gubitak i žalovanje", helper: "tuga, prekid, smrt bliske osobe" },
  { value: "parenting", label: "Roditeljstvo", helper: "dijete, adolescent, roditeljske dileme" },
  { value: "addiction", label: "Ovisnost", helper: "alkohol, supstance, obrasci ponašanja" },
  { value: "sexual_problems", label: "Seksualnost", helper: "intimnost, želja, seksualne poteškoće" },
  { value: "gender_identity", label: "Rodni identitet", helper: "pitanja roda, prihvatanje sebe i okoline" },
  { value: "infidelity", label: "Prevara u odnosu", helper: "nevjera, narušeno povjerenje, šta dalje" },
  { value: "friendships", label: "Prijateljstva", helper: "usamljenost, konflikti, gubitak prijatelja" },
  { value: "communication", label: "Komunikacija", helper: "izražavanje potreba, svađe, asertivnost" },
  { value: "uncertainty", label: "Nisam siguran/na", helper: "znate da treba podrška, ali tema nije jasna" },
];

const POPULATION_OPTIONS: ChoiceOption<MatchingQuestionnaireAnswers["population"]>[] = [
  { value: "self", label: "Za mene", helper: "individualna podrška za odraslu osobu" },
  { value: "child", label: "Za dijete ili tinejdžera", helper: "podrška za mlađu osobu uz roditelja/staratelja" },
  { value: "couple_family", label: "Za par ili porodicu", helper: "odnosi, komunikacija i zajednički rad" },
];

const IMPACT_OPTIONS: ChoiceOption<MatchingQuestionnaireAnswers["impact"]>[] = [
  { value: "low", label: "Blago", helper: "primijetim, ali funkcionišem većinu dana" },
  { value: "moderate", label: "Umjereno", helper: "utiče na raspoloženje, odnose ili obaveze" },
  { value: "high", label: "Jako", helper: "teško mi je održati svakodnevni ritam" },
  { value: "very_high", label: "Vrlo teško", helper: "treba mi brža i pažljivija podrška" },
];

const DURATION_OPTIONS: ChoiceOption<MatchingQuestionnaireAnswers["duration"]>[] = [
  { value: "less_than_month", label: "Kraće od mjesec", helper: "novije ili iznenadno" },
  { value: "one_to_six_months", label: "1 do 6 mjeseci", helper: "traje već neko vrijeme" },
  { value: "more_than_six_months", label: "Više od 6 mjeseci", helper: "postalo je dio svakodnevice" },
  { value: "years", label: "Godinama", helper: "duži obrazac ili ponavljajuća tema" },
];

const SELF_HARM_OPTIONS: ChoiceOption<MatchingQuestionnaireAnswers["selfHarm"]>[] = [
  { value: "no", label: "Ne", helper: "nemam misli da sebi naudim" },
  { value: "passive", label: "Ponekad mi dođe misao", helper: "bez plana, ali želim da to neko zna" },
  { value: "active_plan", label: "Imam plan ili namjeru", helper: "potrebna je hitna podrška i sigurnosni korak" },
];

const STYLE_OPTIONS: ChoiceOption<MatchingQuestionnaireAnswers["preferredStyle"]>[] = [
  { value: "structured", label: "Jasna struktura", helper: "konkretni koraci, alati i fokus" },
  { value: "supportive", label: "Topao razgovor", helper: "siguran prostor, razumijevanje i tempo po vama" },
  { value: "emotional_depth", label: "Dublji rad", helper: "emocije, obrasci, prošla iskustva" },
  { value: "not_sure", label: "Nisam siguran/na", helper: "terapeut može predložiti pristup" },
];

const HOMEWORK_OPTIONS: ChoiceOption<MatchingQuestionnaireAnswers["homework"]>[] = [
  { value: "yes", label: "Da", helper: "želim vježbe ili zadatke između susreta" },
  { value: "no", label: "Ne sada", helper: "više mi odgovara razgovor u terminu" },
  { value: "either", label: "Svejedno", helper: "otvoren/a sam za prijedlog terapeuta" },
];

const MODALITY_OPTIONS: ChoiceOption<MatchingQuestionnaireAnswers["modality"]>[] = [
  { value: "online", label: "Online", helper: "video susret od kuće ili s posla" },
  { value: "in_person", label: "Uživo", helper: "termin u prostoru terapeuta" },
  { value: "any", label: "Svejedno", helper: "najbitniji su dobar terapeut i prvi slobodan termin" },
];

const TIME_OPTIONS: ChoiceOption<MatchingQuestionnaireAnswers["timePreferences"][number]>[] = [
  { value: "morning", label: "Jutro", helper: "prije dnevnih obaveza" },
  { value: "afternoon", label: "Popodne", helper: "u toku radnog dana" },
  { value: "evening", label: "Večer", helper: "nakon posla ili škole" },
  { value: "weekend", label: "Vikend", helper: "subota ili nedjelja" },
  { value: "flexible", label: "Fleksibilno", helper: "mogu se prilagoditi" },
];

const BUDGET_OPTIONS: ChoiceOption<MatchingQuestionnaireAnswers["budget"]>[] = [
  { value: "no_limit", label: "Nije presudno", helper: "prioritet je najbolji match" },
  { value: "up_to_50", label: "Do 50 KM", helper: "tražimo dostupnije opcije" },
  { value: "up_to_80", label: "Do 80 KM", helper: "uravnotežen izbor" },
  { value: "up_to_120", label: "Do 120 KM", helper: "širi izbor terapeuta" },
  { value: "company_covered", label: "Pokriva firma", helper: "koristit ću poslovni benefit" },
];

export function MatchingQuestionnaire({
  onSubmit,
  isSubmitting,
  initialAnswers,
  resumeAtEnd = false,
  initialStarted = false,
}: MatchingQuestionnaireProps) {
  const panelRef = useRef<HTMLElement>(null);
  const advanceTimerRef = useRef<number | null>(null);
  const [hasStarted, setHasStarted] = useState(initialStarted);
  const [questionIndex, setQuestionIndex] = useState(resumeAtEnd ? QUESTIONS.length - 1 : 0);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [answers, setAnswers] = useState<MatchingQuestionnaireAnswers>(initialAnswers ?? DEFAULT_ANSWERS);

  const question = QUESTIONS[questionIndex];
  const step = question.stepIndex;
  const currentStep = STEPS[step];
  const lastQuestion = questionIndex === QUESTIONS.length - 1;
  const progress = ((questionIndex + 1) / QUESTIONS.length) * 100;
  const selectedSecondaryConcerns = answers.secondaryConcerns ?? [];

  useEffect(() => {
    return () => clearAdvanceTimer(advanceTimerRef);
  }, []);

  useEffect(() => {
    if (initialStarted) scrollPanelIntoView(panelRef.current);
  }, [initialStarted]);

  function update<K extends keyof MatchingQuestionnaireAnswers>(
    key: K,
    value: MatchingQuestionnaireAnswers[K],
  ) {
    setAnswers((current) => ({ ...current, [key]: value }));
  }

  function selectPrimaryConcern(primaryConcern: MatchingQuestionnaireAnswers["primaryConcern"]) {
    setAnswers((current) => ({
      ...current,
      primaryConcern,
      secondaryConcerns: (current.secondaryConcerns ?? []).filter((concern) => concern !== primaryConcern),
    }));
  }

  function toggleSecondaryConcern(concern: MatchingQuestionnaireAnswers["primaryConcern"]) {
    setAnswers((current) => {
      const existing = current.secondaryConcerns ?? [];
      if (existing.includes(concern)) {
        return { ...current, secondaryConcerns: existing.filter((item) => item !== concern) };
      }
      if (existing.length >= 3) return current;
      return { ...current, secondaryConcerns: [...existing, concern] };
    });
  }

  function toggleTimePreference(value: MatchingQuestionnaireAnswers["timePreferences"][number]) {
    setAnswers((current) => {
      const selected = current.timePreferences;
      if (selected.includes(value)) {
        if (selected.length === 1) return current;
        return { ...current, timePreferences: selected.filter((item) => item !== value) };
      }
      return { ...current, timePreferences: [...selected, value] };
    });
  }

  function moveToQuestion(nextIndex: number, nextDirection: "forward" | "back") {
    clearAdvanceTimer(advanceTimerRef);
    setDirection(nextDirection);
    setQuestionIndex(Math.max(0, Math.min(nextIndex, QUESTIONS.length - 1)));
    scrollPanelIntoView(panelRef.current);
  }

  function scheduleAdvance() {
    if (lastQuestion || typeof window === "undefined") return;
    clearAdvanceTimer(advanceTimerRef);
    advanceTimerRef.current = window.setTimeout(() => {
      advanceTimerRef.current = null;
      moveToQuestion(questionIndex + 1, "forward");
    }, 220);
  }

  async function continueFlow() {
    if (lastQuestion) {
      await onSubmit(answers);
      return;
    }
    moveToQuestion(questionIndex + 1, "forward");
  }

  function renderQuestion(id: QuestionId) {
    switch (id) {
      case "population":
        return (
          <ChoiceGroup
            options={POPULATION_OPTIONS}
            value={answers.population}
            onChange={(value) => {
              update("population", value);
              scheduleAdvance();
            }}
            columns="three"
          />
        );
      case "primaryConcern":
        return (
          <ChoiceGroup
            options={CONCERNS}
            value={answers.primaryConcern}
            onChange={(value) => {
              selectPrimaryConcern(value);
              scheduleAdvance();
            }}
            columns="two"
          />
        );
      case "secondaryConcerns":
        return (
          <MultiChoiceGroup
            options={CONCERNS.filter((option) => option.value !== answers.primaryConcern)}
            values={selectedSecondaryConcerns}
            onToggle={toggleSecondaryConcern}
            maxSelected={3}
          />
        );
      case "impact":
        return (
          <ChoiceGroup
            options={IMPACT_OPTIONS}
            value={answers.impact}
            onChange={(value) => {
              update("impact", value);
              scheduleAdvance();
            }}
            columns="two"
          />
        );
      case "duration":
        return (
          <ChoiceGroup
            options={DURATION_OPTIONS}
            value={answers.duration}
            onChange={(value) => {
              update("duration", value);
              scheduleAdvance();
            }}
            columns="two"
          />
        );
      case "selfHarm":
        return (
          <ChoiceGroup
            options={SELF_HARM_OPTIONS}
            value={answers.selfHarm}
            onChange={(value) => update("selfHarm", value)}
            columns="one"
          />
        );
      case "preferredStyle":
        return (
          <ChoiceGroup
            options={STYLE_OPTIONS}
            value={answers.preferredStyle}
            onChange={(value) => {
              update("preferredStyle", value);
              scheduleAdvance();
            }}
            columns="two"
          />
        );
      case "homework":
        return (
          <ChoiceGroup
            options={HOMEWORK_OPTIONS}
            value={answers.homework}
            onChange={(value) => {
              update("homework", value);
              scheduleAdvance();
            }}
            columns="three"
          />
        );
      case "modality":
        return (
          <ChoiceGroup
            options={MODALITY_OPTIONS}
            value={answers.modality}
            onChange={(value) => {
              update("modality", value);
              scheduleAdvance();
            }}
            columns="three"
          />
        );
      case "timePreferences":
        return (
          <MultiChoiceGroup
            options={TIME_OPTIONS}
            values={answers.timePreferences}
            onToggle={toggleTimePreference}
          />
        );
      case "budget":
        return (
          <ChoiceGroup
            options={BUDGET_OPTIONS}
            value={answers.budget}
            onChange={(value) => {
              update("budget", value);
              scheduleAdvance();
            }}
            columns="two"
          />
        );
      case "goals":
        return (
          <div className="space-y-3">
            <Textarea
              value={answers.goals ?? ""}
              onChange={(event) => update("goals", event.target.value)}
              maxLength={1000}
              placeholder="Npr. želim manje panike, bolje granice u odnosima ili da opet mogu normalno spavati..."
              className="min-h-36 rounded-2xl border-[oklch(86%_0.025_84)] bg-white/75 p-4 text-sm shadow-none placeholder:text-muted-foreground/60 focus-visible:border-[oklch(37.12%_0.059_186.62)] focus-visible:ring-[oklch(37.12%_0.059_186.62)]/20"
            />
          </div>
        );
    }
  }

  if (!hasStarted) {
    return (
      <IntroScreen
        onStart={() => {
          setHasStarted(true);
          scrollToPageStart();
        }}
      />
    );
  }

  return (
    <main className="min-h-[calc(100svh-3.5rem)] bg-[oklch(95.43%_0.0136_78.26)] px-0 py-0 text-foreground sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto grid max-w-6xl gap-0 sm:gap-6 lg:grid-cols-[0.88fr_1.12fr] lg:items-start">
        <aside className="order-2 rounded-none border-x-0 border-y border-[oklch(86%_0.025_84)] bg-white/70 p-4 shadow-[0_22px_60px_-42px_rgba(41,35,27,0.5)] backdrop-blur sm:rounded-3xl sm:border lg:order-1 lg:sticky lg:top-6 lg:rounded-[2rem] lg:p-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-[oklch(84%_0.035_84)] bg-[oklch(98%_0.005_84)] px-3 py-1.5 text-xs font-semibold text-[oklch(37.12%_0.059_186.62)]">
            <LockKeyhole className="size-3.5" />
            Privatan početak
          </div>

          <div className="mt-4 space-y-3 lg:mt-7 lg:space-y-4">
            <h1 className="text-balance text-2xl font-semibold leading-tight tracking-normal text-[oklch(25%_0.04_84)] sm:text-3xl lg:text-4xl">
              Pronađi terapeuta za sebe
            </h1>
            <p className="text-sm leading-6 text-muted-foreground lg:text-base lg:leading-7">
              Pitanja su lična, ali kratka. Koristimo ih da razumijemo temu, intenzitet i način rada
              koji bi vam mogao odgovarati.
            </p>
          </div>

          <div className="mt-4 flex gap-3 rounded-2xl border border-[oklch(88%_0.025_84)] bg-white/65 p-3 text-sm leading-6 text-[oklch(42%_0.035_84)] lg:hidden">
            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-[oklch(37.12%_0.059_186.62)]" />
            <span>Svako pitanje ima svrhu: da brže dođete do dva terapeuta koja možete rezervisati.</span>
          </div>

          <div className="mt-8 hidden space-y-3 lg:block">
            <TrustRow
              icon={<ShieldCheck className="size-4" />}
              title="Ne tražimo više nego što je potrebno."
              body="Ovdje nema dijagnoze ni dugog formulara. Samo signali koji pomažu preporuci."
            />
            <TrustRow
              icon={<Sparkles className="size-4" />}
              title="Odgovori pomažu da ne lutate kroz profile."
              body="Matching uzima u obzir temu, stil rada, format terapije i dostupne termine."
            />
            <TrustRow
              icon={<HeartHandshake className="size-4" />}
              title="Tu smo da vas usmjerimo ka prvom sigurnom koraku."
              body="Na kraju odmah vidite dva terapeuta i možete rezervisati termin."
            />
          </div>

          <div className="mt-8 hidden rounded-2xl border border-[oklch(87%_0.03_84)] bg-[oklch(98%_0.005_84)] p-4 text-sm leading-6 text-[oklch(42%_0.035_84)] lg:block">
            Ovo nije dijagnoza. To je pažljiv način da vas usmjerimo ka dva terapeuta koji imaju
            najviše smisla za početak.
          </div>
        </aside>

        <section
          ref={panelRef}
          className="order-1 min-h-[calc(100svh-3.5rem)] rounded-none border-x-0 border-y border-[oklch(85%_0.03_84)] bg-white shadow-[0_24px_70px_-44px_rgba(31,47,51,0.7)] sm:min-h-0 sm:rounded-[2rem] sm:border lg:order-2"
        >
          <div className="border-b border-[oklch(89%_0.025_84)] bg-[oklch(98%_0.005_84)] px-4 py-4 sm:rounded-t-[2rem] sm:px-7 sm:py-5">
            <StepProgress activeStep={step} />
            <div className="mt-3 flex items-center justify-between gap-3 text-xs font-semibold text-[oklch(47%_0.04_84)]">
              <span>{currentStep.label}</span>
              <span>
                Pitanje {questionIndex + 1} od {QUESTIONS.length}
              </span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-[oklch(89%_0.025_84)]">
              <div
                className="h-full rounded-full bg-[oklch(37.12%_0.059_186.62)] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="px-4 py-5 pb-24 sm:px-7 sm:py-8 sm:pb-8">
            <div className="mb-5 sm:mb-7">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[oklch(55%_0.07_84)]">
                {currentStep.label} · pitanje {questionIndex + 1}/{QUESTIONS.length}
              </p>
              <h2 className="mt-2 text-2xl font-semibold leading-tight text-[oklch(25%_0.04_84)]">
                {question.title}
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
                {question.description}
              </p>
              <div className="mt-3 flex gap-2 rounded-2xl border border-[oklch(88%_0.03_84)] bg-[oklch(97%_0.01_84)] p-3 text-xs leading-5 text-[oklch(40%_0.04_84)] sm:mt-4 sm:gap-3 sm:p-4 sm:text-sm sm:leading-6">
                <LockKeyhole className="mt-0.5 size-4 shrink-0 text-[oklch(37.12%_0.059_186.62)]" />
                <span>{question.privacy}</span>
              </div>
            </div>

            <div
              key={question.id}
              aria-live="polite"
              className={cn(
                "min-h-[18rem] animate-in fade-in duration-200 sm:min-h-[23rem]",
                direction === "forward" ? "slide-in-from-right-3" : "slide-in-from-left-3",
              )}
            >
              {renderQuestion(question.id)}
            </div>

            <div className="sticky bottom-0 z-10 -mx-4 mt-6 flex flex-col-reverse gap-3 border-t border-[oklch(90%_0.025_84)] bg-white/95 px-4 py-3 shadow-[0_-18px_36px_-28px_rgba(31,47,51,0.7)] backdrop-blur sm:static sm:mx-0 sm:mt-8 sm:flex-row sm:items-center sm:justify-between sm:bg-transparent sm:px-0 sm:py-0 sm:pt-5 sm:shadow-none sm:backdrop-blur-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  moveToQuestion(questionIndex - 1, "back");
                }}
                disabled={questionIndex === 0 || isSubmitting}
                className="h-11 rounded-full border-[oklch(84%_0.03_84)] bg-white px-5 text-[oklch(35%_0.04_84)] hover:bg-[oklch(98%_0.005_84)]"
              >
                <ArrowLeft className="size-4" />
                Nazad
              </Button>
              <Button
                type="button"
                onClick={continueFlow}
                disabled={isSubmitting}
                className="h-11 rounded-full bg-[oklch(37.12%_0.059_186.62)] px-6 text-white shadow-[0_16px_32px_-20px_rgba(8,45,42,0.8)] hover:bg-[oklch(29.52%_0.0501_186.62)]"
              >
                {isSubmitting ? (
                  <>
                    <LoaderCircle className="size-4 animate-spin" />
                    Tražimo terapeute
                  </>
                ) : lastQuestion ? (
                  <>
                    Prikaži preporuke
                    <Sparkles className="size-4" />
                  </>
                ) : (
                  <>
                    Nastavi
                    <ArrowRight className="size-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function scrollPanelIntoView(panel: HTMLElement | null) {
  if (typeof window === "undefined" || !panel) return;
  window.requestAnimationFrame(() => {
    panel.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

function scrollToPageStart() {
  if (typeof window === "undefined") return;
  window.requestAnimationFrame(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  });
}

function clearAdvanceTimer(timerRef: React.MutableRefObject<number | null>) {
  if (typeof window === "undefined" || timerRef.current === null) return;
  window.clearTimeout(timerRef.current);
  timerRef.current = null;
}

function IntroScreen({ onStart }: { onStart: () => void }) {
  return (
    <main className="min-h-[calc(100svh-3.5rem)] bg-[oklch(95.43%_0.0136_78.26)] p-0 text-foreground sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100svh-3.5rem)] max-w-3xl items-stretch sm:min-h-0 sm:items-center">
        <section className="flex min-h-[calc(100svh-3.5rem)] w-full flex-col justify-between border-0 bg-white/72 px-6 py-7 shadow-[0_24px_70px_-46px_rgba(41,35,27,0.55)] sm:min-h-0 sm:rounded-[2rem] sm:border sm:border-[oklch(84%_0.03_84)] sm:p-8 lg:p-10">
          <div>
            <div className="mt-8 space-y-5 sm:mt-10">
              <h1 className="text-balance text-4xl font-semibold leading-tight tracking-normal text-[oklch(25%_0.04_84)] sm:text-5xl">
                Pronađi terapeuta za sebe
              </h1>
              <p className="max-w-2xl text-lg leading-8 text-[oklch(42%_0.035_84)] sm:text-xl sm:leading-9">
                Pitanja su lična, ali kratka. Koristimo ih da razumijemo temu, intenzitet i način rada
                koji bi vam mogao odgovarati.
              </p>
            </div>

            <div className="mt-9 flex gap-4 rounded-3xl border border-[oklch(84%_0.03_84)] bg-[oklch(99%_0.003_84)] p-5 text-base leading-7 text-[oklch(42%_0.035_84)] sm:p-6 sm:text-lg sm:leading-8">
              <ShieldCheck className="mt-1 size-6 shrink-0 text-[oklch(37.12%_0.059_186.62)]" />
              <span>
                Svako pitanje ima svrhu: da brže dođete do dva terapeuta koja možete rezervisati.
              </span>
            </div>
          </div>

          <Button
            type="button"
            onClick={onStart}
            className="mt-8 h-13 w-full rounded-full bg-[oklch(37.12%_0.059_186.62)] px-6 text-base font-semibold text-white shadow-[0_18px_36px_-22px_rgba(8,45,42,0.8)] hover:bg-[oklch(29.52%_0.0501_186.62)] sm:mt-10 sm:w-auto sm:self-start sm:px-8"
          >
            Kreni
            <ArrowRight className="size-5" />
          </Button>
        </section>
      </div>
    </main>
  );
}

function TrustRow({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex gap-3 rounded-2xl border border-[oklch(88%_0.025_84)] bg-white/65 p-4">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[oklch(37.12%_0.059_186.62)]/10 text-[oklch(37.12%_0.059_186.62)]">
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold text-[oklch(25%_0.04_84)]">{title}</p>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

function StepProgress({ activeStep }: { activeStep: number }) {
  return (
    <ol className="grid grid-cols-4 gap-1.5 sm:gap-2">
      {STEPS.map((item, index) => {
        const active = index === activeStep;
        const complete = index < activeStep;
        return (
          <li key={item.label}>
            <div
              className={cn(
                "flex min-h-10 flex-col justify-center rounded-xl border px-2 py-2 transition-all sm:min-h-16 sm:rounded-2xl sm:px-3",
                active || complete
                  ? "border-[oklch(37.12%_0.059_186.62)] bg-white text-[oklch(21.52%_0.0365_186.62)]"
                  : "border-[oklch(88%_0.025_84)] bg-white/55 text-muted-foreground",
              )}
            >
              <div
                className={cn(
                  "mx-auto flex size-6 items-center justify-center rounded-full text-xs font-semibold sm:mx-0 sm:mb-1",
                  active || complete
                    ? "bg-[oklch(37.12%_0.059_186.62)] text-white"
                    : "bg-[oklch(91%_0.025_84)] text-[oklch(50%_0.035_84)]",
                )}
              >
                {complete ? <Check className="size-3.5" /> : index + 1}
              </div>
              <span className="hidden truncate text-xs font-semibold sm:block">{item.label}</span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function ChoiceGroup<T extends string>({
  title,
  description,
  options,
  value,
  onChange,
  columns = "two",
}: {
  title?: string;
  description?: string;
  options: ChoiceOption<T>[];
  value: T;
  onChange: (value: T) => void;
  columns?: "one" | "two" | "three";
}) {
  return (
    <div className="space-y-3">
      {title ? <QuestionHeader title={title} description={description} /> : null}
      <div className={choiceGridClass(columns)}>
        {options.map((option) => (
          <ChoiceButton
            key={option.value}
            label={option.label}
            helper={option.helper}
            selected={option.value === value}
            onClick={() => onChange(option.value)}
          />
        ))}
      </div>
    </div>
  );
}

function MultiChoiceGroup<T extends string>({
  title,
  description,
  options,
  values,
  onToggle,
  maxSelected,
}: {
  title?: string;
  description?: string;
  options: ChoiceOption<T>[];
  values: T[];
  onToggle: (value: T) => void;
  maxSelected?: number;
}) {
  return (
    <div className="space-y-3">
      {title ? (
        <QuestionHeader
          title={title}
          description={
            maxSelected ? `${description ?? ""} Odabrano ${values.length}/${maxSelected}.`.trim() : description
          }
        />
      ) : maxSelected ? (
        <p className="text-xs font-semibold text-[oklch(47%_0.04_84)]">
          Odabrano {values.length}/{maxSelected}
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        {options.map((option) => (
          <ChoiceButton
            key={option.value}
            label={option.label}
            helper={option.helper}
            selected={values.includes(option.value)}
            onClick={() => onToggle(option.value)}
          />
        ))}
      </div>
    </div>
  );
}

function QuestionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-[oklch(25%_0.04_84)]">{title}</h3>
      {description ? <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p> : null}
    </div>
  );
}

function ChoiceButton({
  label,
  helper,
  selected,
  onClick,
}: {
  label: string;
  helper?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        "group flex min-h-[3.75rem] w-full items-start justify-between gap-3 rounded-2xl border px-4 py-3 text-left text-sm transition-all sm:min-h-20",
        selected
          ? "border-[oklch(37.12%_0.059_186.62)] bg-[oklch(37.12%_0.059_186.62)]/10 text-[oklch(25%_0.04_84)] shadow-[0_14px_30px_-24px_rgba(8,45,42,0.8)]"
          : "border-[oklch(87%_0.025_84)] bg-white/75 text-foreground hover:border-[oklch(37.12%_0.059_186.62)]/45 hover:bg-white",
      )}
    >
      <span className="min-w-0">
        <span className="block font-semibold leading-5">{label}</span>
        {helper ? (
          <span className={cn("mt-1 text-xs leading-5 text-muted-foreground", selected ? "block" : "hidden sm:block")}>
            {helper}
          </span>
        ) : null}
      </span>
      <span
        className={cn(
          "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border transition-colors",
          selected
            ? "border-[oklch(37.12%_0.059_186.62)] bg-[oklch(37.12%_0.059_186.62)] text-white"
            : "border-[oklch(84%_0.025_84)] bg-white text-transparent group-hover:text-[oklch(37.12%_0.059_186.62)]",
        )}
      >
        <Check className="size-3.5" />
      </span>
    </button>
  );
}

function choiceGridClass(columns: "one" | "two" | "three") {
  if (columns === "one") return "grid gap-3";
  if (columns === "three") return "grid gap-3 sm:grid-cols-3";
  return "grid gap-3 sm:grid-cols-2";
}
